// useRecipe.js
import { useState, useEffect } from "react";
import useApiRequest from "./useApiRequest";

const useRecipe = (recipeId) => {
  const [recipe, setRecipe] = useState(null);
  const [currentServings, setCurrentServings] = useState(null);
  const { makeApiRequest } = useApiRequest();

  const niceFractions = [
    { fraction: "1/8", decimal: 1 / 8 },
    { fraction: "1/6", decimal: 1 / 6 },
    { fraction: "1/4", decimal: 1 / 4 },
    { fraction: "1/3", decimal: 1 / 3 },
    { fraction: "3/8", decimal: 3 / 8 },
    { fraction: "1/2", decimal: 1 / 2 },
    { fraction: "5/8", decimal: 5 / 8 },
    { fraction: "2/3", decimal: 2 / 3 },
    { fraction: "3/4", decimal: 3 / 4 },
    { fraction: "5/6", decimal: 5 / 6 },
    { fraction: "7/8", decimal: 7 / 8 },
  ];

  const findClosestFraction = (decimal) => {
    return niceFractions.reduce((prev, curr) =>
      Math.abs(curr.decimal - decimal) < Math.abs(prev.decimal - decimal)
        ? curr
        : prev
    ).fraction;
  };

  const formatAmount = (amount) => {
    const wholePart = Math.floor(amount);
    const decimalPart = amount - wholePart;

    if (decimalPart === 0) {
      return wholePart.toString();
    } else {
      const closestFraction = findClosestFraction(decimalPart);
      return wholePart > 0
        ? `${wholePart} ${closestFraction}`
        : closestFraction;
    }
  };

  const adjustIngredients = (newServings) => {
    if (!recipe) {
      console.log("No recipe found");
      return [];
    }

    const adjustedSteps = recipe.steps.map((step, stepIndex) => {
      const updatedIngredients = step.ingredients.map(
        (ingredient, ingredientIndex) => {
          const updatedAmount =
            (ingredient.amount / recipe.servings) * newServings;
          const formattedAmount = formatAmount(updatedAmount);

          return {
            ...ingredient,
            amount: updatedAmount,
            formattedAmount: formattedAmount,
          };
        }
      );

      return {
        ...step,
        ingredients: updatedIngredients,
      };
    });

    return adjustedSteps;
  };

  useEffect(() => {
    if (recipe && currentServings) {
      console.log("got here");
      const adjustedSteps = adjustIngredients(currentServings);
      const updatedRecipe = {
        ...recipe,
        steps: adjustedSteps,
        servings: currentServings,
      };
      setRecipe(updatedRecipe);
    }
  }, [currentServings]);

  useEffect(() => {
    let isMounted = true;

    const fetchRecipe = async () => {
      try {
        // Temporarily set currentServings to null to ensure a change is detected
        setCurrentServings(null);

        const response = await makeApiRequest(
          `/api/recipe/${recipeId}/`,
          "GET"
        );
        if (isMounted && response) {
          setRecipe(response.data);

          // Now set currentServings to the actual value, forcing a re-render
          setCurrentServings(response.data.servings);
        }
      } catch (error) {
        if (isMounted) {
          console.error("Error fetching recipe:", error);
        }
      }
    };

    if (recipeId) {
      fetchRecipe();
    }

    return () => {
      isMounted = false; // Cleanup function
    };
  }, [recipeId, makeApiRequest]);

  return { recipe, currentServings, setCurrentServings };
};

export default useRecipe;
